// src/pages/public/TermsAndCondition.jsx
import React from "react";
import { Box, Container, Typography, Divider, Paper } from "@mui/material";
import { useThemeContext } from "./context/ThemeContext";


export default function TermsAndCondition() {
  const { mode } = useThemeContext();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor:
          mode === "dark"
            ? "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
            : "linear-gradient(135deg, #f8fafc 0%, #e0e7ef 100%)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Container
        maxWidth="md"
        sx={{
          flex: 1,
          py: { xs: 4, md: 6 },
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 3,
            bgcolor: mode === "dark" ? "rgba(30,41,59,0.9)" : "#fff",
            boxShadow:
              mode === "dark"
                ? "0 4px 20px rgba(0,0,0,0.6)"
                : "0 4px 20px rgba(0,0,0,0.1)",
          }}
        >
          <Typography
            variant="h4"
            fontWeight={700}
            gutterBottom
            sx={{ textAlign: "center" }}
          >
            Terms & Conditions
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            gutterBottom
            sx={{ textAlign: "center", mb: 4 }}
          >
            Please read these Terms & Conditions carefully before using our
            platform. By accessing or using our services, you acknowledge that
            you have read, understood, and agree to be bound by these terms.
          </Typography>

          {/* Sections */}
          {/* ✅ Acceptance of Terms */}
          <Box mb={4}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              1. Acceptance of Terms
            </Typography>
            <Typography variant="body2" color="text.secondary">
              By registering for an account, accessing, or using our services,
              you agree to comply with and be legally bound by these Terms &amp;
              Conditions and all applicable laws and regulations. If you do not
              agree with any part of these terms, you must not use our platform.
              <br />
              <br />
              These Terms form a legally binding agreement between you and the
              platform. Continued use of the services after updates means you
              accept the revised version of these Terms.
            </Typography>
          </Box>
          <Divider />

          {/* ✅ Eligibility */}
          <Box my={4}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              2. Eligibility
            </Typography>
            <Typography variant="body2" color="text.secondary">
              To use our platform, you must be at least 18 years old or the age
              of majority in your jurisdiction. By creating an account, you
              represent and warrant that you meet this requirement. We reserve
              the right to refuse access to anyone at our sole discretion.
            </Typography>
          </Box>
          <Divider />

          {/* ✅ User Responsibilities */}
          <Box my={4}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              3. User Responsibilities
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You agree to use our services responsibly and not to:
              <ul>
                <li>Engage in fraudulent, illegal, or abusive activities.</li>
                <li>
                  Share false, misleading, or deceptive information during
                  registration or KYC.
                </li>
                <li>
                  Attempt to gain unauthorized access to other user accounts,
                  systems, or networks.
                </li>
                <li>
                  Use automated scripts, bots, or unauthorized tools to interact
                  with the platform.
                </li>
              </ul>
              You are solely responsible for safeguarding your account
              credentials and any activity under your account.
            </Typography>
          </Box>
          <Divider />

          {/* ✅ KYC */}
          <Box my={4}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              4. KYC & Compliance
            </Typography>
            <Typography variant="body2" color="text.secondary">
              To comply with international regulations, users must complete the
              Know Your Customer (KYC) verification process. Failure to complete
              KYC may limit your access to certain features, including
              withdrawals, transactions, and account upgrades.
              <br />
              <br />
              Submission of forged, altered, or invalid documents is strictly
              prohibited and may result in account suspension or permanent ban
              without notice.
            </Typography>
          </Box>
          <Divider />

          {/* ✅ Account Suspension */}
          <Box my={4}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              5. Account Suspension & Termination
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We may suspend, restrict, or terminate your account if we suspect
              violation of these Terms, fraudulent behavior, or unlawful
              activities. In serious cases, legal authorities may be notified.
              <br />
              <br />
              You may close your account at any time by contacting support, but
              you remain responsible for any pending obligations or
              transactions.
            </Typography>
          </Box>
          <Divider />

          {/* ✅ Fees */}
          <Box my={4}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              6. Fees & Payments
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Some services on our platform may involve fees or charges. All
              applicable fees will be disclosed prior to completing a
              transaction. By proceeding, you agree to pay all relevant charges.
              <br />
              <br />
              We are not responsible for losses due to incorrect payment details
              provided by the user.
            </Typography>
          </Box>
          <Divider />

          {/* ✅ Limitation */}
          <Box my={4}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              7. Limitation of Liability
            </Typography>
            <Typography variant="body2" color="text.secondary">
              To the maximum extent permitted by law, our platform shall not be
              held liable for:
              <ul>
                <li>Losses due to unauthorized access to your account.</li>
                <li>Service interruptions, delays, or downtime.</li>
                <li>Indirect, incidental, or consequential damages.</li>
              </ul>
              Your use of the platform is entirely at your own risk.
            </Typography>
          </Box>
          <Divider />

          {/* ✅ IP */}
          <Box my={4}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              8. Intellectual Property
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All content, trademarks, logos, and intellectual property
              associated with our platform remain the exclusive property of the
              company. You are not permitted to copy, modify, distribute, or
              reproduce any part of the platform without written consent.
            </Typography>
          </Box>
          <Divider />

          {/* ✅ Updates */}
          <Box my={4}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              9. Updates to Terms
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We reserve the right to revise or update these Terms &amp;
              Conditions at any time. Updates will be effective once posted on
              the platform. It is your responsibility to review the Terms
              regularly. Continued use of the platform constitutes acceptance of
              the latest version.
            </Typography>
          </Box>
          <Divider />

          {/* ✅ Privacy Policy */}
          <Box my={4}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              10. Privacy Policy
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We respect your privacy and are committed to protecting your
              personal information. Any data collected through the platform will
              be used solely for the purposes of providing services, verifying
              identity (KYC), ensuring security, and complying with legal
              requirements. <br />
              <br />
              We do not sell, rent, or trade your personal information to third
              parties. However, we may share data with trusted partners only to
              the extent necessary for service delivery, compliance, and fraud
              prevention. <br />
              <br />
              By using our platform, you consent to the collection and use of
              information in accordance with this Privacy Policy. For details on
              how your data is handled, please contact our support team.
            </Typography>
          </Box>

          {/* ✅ Last updated */}
          <Typography
            variant="body2"
            sx={{ mt: 4, textAlign: "center", fontStyle: "italic" }}
            color="text.secondary"
          >
            Last updated: September 2025
          </Typography>
        </Paper>
      </Container>

      
    </Box>
  );
}
